import { useCallback, useState } from 'react';

export type SortDirection = 'asc' | 'desc';

export function compareSortValues(
  a: string | number,
  b: string | number,
  direction: SortDirection,
): number {
  const factor = direction === 'asc' ? 1 : -1;
  if (typeof a === 'number' && typeof b === 'number') {
    if (a === b) return 0;
    return a < b ? -factor : factor;
  }
  return String(a).localeCompare(String(b), undefined, { sensitivity: 'base' }) * factor;
}

export function useTableSort<K extends string>(
  defaultKey: K,
  defaultDirection: SortDirection = 'asc',
) {
  const [sortKey, setSortKey] = useState<K>(defaultKey);
  const [sortDirection, setSortDirection] = useState<SortDirection>(defaultDirection);

  const toggleSort = useCallback(
    (key: K) => {
      if (key === sortKey) {
        setSortDirection((direction) => (direction === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortKey(key);
        setSortDirection('asc');
      }
    },
    [sortKey],
  );

  const sortRows = useCallback(
    <T,>(rows: T[], accessors: Record<K, (row: T) => string | number>) => {
      const accessor = accessors[sortKey];
      return [...rows].sort((a, b) => compareSortValues(accessor(a), accessor(b), sortDirection));
    },
    [sortKey, sortDirection],
  );

  return { sortKey, sortDirection, toggleSort, sortRows };
}
